import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import {
  resolveTranslationUsername,
  translationErrorMessage,
  translationErrorProduct,
} from "../traduzioni/translationRequest";
import prodVuoto from "../prodottoVuoto";
import { isUtenteSpeciale } from "./permessiProdotto";
import useApriProdottoSolaLettura from "./useApriProdottoSolaLettura";
import stilePulsanti from "../interfaccia/stilePulsanti";
import _ from "lodash";
import {
  applicaCambioSpedizione,
  FASCIA_SPEDIZIONE_PATH,
  METODO_SPEDIZIONE_PATH,
} from "./spedizioneIngombranti";
import { useAuth0 } from "@auth0/auth0-react";
import umanizzaDataSenzaSecondi from "../interfaccia/funzioniComuni/umanizzaDataSenzaSecondi";
import { useLocation, useNavigate } from "react-router-dom";
import ModaArticoliAdvancedFiltersModal from "../moda/ModaArticoliAdvancedFiltersModal";
import PrestashopAlignmentItemReview from "../moda/PrestashopAlignmentItemReview";
import ColonnaDatiProdotto from "./ColonnaDatiProdotto";
import Categorie from "./pagine/Categorie";
import Immagini from "./pagine/Immagini";
import Descrizioni from "./pagine/Descrizioni";
import TestoIntro from "./pagine/TestoIntro";
import Traduzioni from "./pagine/Traduzioni";
import DatiAltrove from "./pagine/DatiAltrove";
import Impostazioni from "./pagine/Impostazioni";
import KitModa from "./pagine/KitModa";
import "./ModificaProdottiNEW.css";
import {
  publicationLabelFromValue,
  publicationValueFromSelection,
} from "../moda/publicationStatus";

const PUBBLICATO_PROMOTED_VALUES = new Set([
  "SI",
  "PROBLEMA",
  "NON PUBBLICARE",
]);

const pickFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value !== "string") {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed.length) {
      return trimmed;
    }
  }
  return "";
};

const getProductIdentity = (product) =>
  product?._id ?? product?.codiceVS ?? "__NO_ID__";

const canViewMultisiteSupportForUser = (utente, uiFeatureFlags) =>
  Boolean(utente?.permessiAPI?.["read:debug"]) ||
  Boolean(uiFeatureFlags?.loaded && !uiFeatureFlags?.nascondiSupportoMultisito);

const normalizeCategoryId = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const parsed = Number.parseInt(String(value), 10);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
};

const normalizeTinyIntFlag = (value, fallback = 0) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const hasImportBotFlag = (record) =>
  normalizeTinyIntFlag(record?.mostraImportBot ?? record?.mostra_import_bot) === 1;

const hasExportVigFlag = (record) =>
  normalizeTinyIntFlag(record?.mostraExportVig ?? record?.mostra_export_vig) === 1;

const PUBLICATION_DESTINATIONS = ["VIG", "BOT"];

const getPublicationDestinations = (record) =>
  PUBLICATION_DESTINATIONS.filter((site) => {
    const value =
      site === "VIG"
        ? record?.pubblicaSuVig ?? record?.pubblica_su_vig
        : record?.pubblicaSuBot ?? record?.pubblica_su_bot;
    return normalizeTinyIntFlag(value, 1) === 1;
  });

const getFirstFilledValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) {
      continue;
    }
    const normalized = String(value).trim();
    if (normalized.length) {
      return normalized;
    }
  }
  return "";
};

const CATEGORY_MAPPING_MODA_FIELDS = [
  { key: "reparto", lookupUrl: "/moda/lookup/reparti" },
  { key: "categoria", lookupUrl: "/moda/lookup/categorie" },
  { key: "tipo", lookupUrl: "/moda/lookup/tipi" },
  { key: "sottotipo", lookupUrl: "/moda/lookup/sottotipi" },
  { key: "persona", lookupUrl: "/moda/lookup/persona" },
];

const CATEGORY_MAPPING_MONGO_FIELDS = [
  {
    key: "stagioneConsigliata",
    section: "generici",
    field: "stagioneConsigliata",
    path: "datiUlteriori.generici.stagioneConsigliata.valueId",
  },
  {
    key: "utilizzo",
    section: "generici",
    field: "utilizzo",
    path: "datiUlteriori.generici.utilizzo.valueId",
  },
  {
    key: "calzatureUtilizzo",
    section: "calzature",
    field: "utilizzo",
    path: "datiUlteriori.calzature.utilizzo.valueId",
  },
  {
    key: "sciUtilizzo",
    section: "sci",
    field: "utilizzo",
    path: "datiUlteriori.sci.utilizzo.valueId",
  },
  {
    key: "skiBootsUtilizzo",
    section: "skiBoots",
    field: "utilizzo",
    path: "datiUlteriori.skiBoots.utilizzo.valueId",
  },
  {
    key: "bastoniSport",
    section: "bastoni",
    field: "sport",
    path: "datiUlteriori.bastoni.sport.valueId",
  },
  {
    key: "bastoniUtilizzo",
    section: "bastoni",
    field: "utilizzo",
    path: "datiUlteriori.bastoni.utilizzo.valueId",
  },
  {
    key: "bikeUtilizzo",
    section: "bike",
    field: "utilizzo",
    path: "datiUlteriori.bike.utilizzo.valueId",
  },
  {
    key: "calzatureAspetto",
    section: "calzature",
    field: "aspetto",
    path: "datiUlteriori.calzature.aspetto.valueId",
  },
  {
    key: "piscinaMareTipo",
    section: "piscinaMare",
    field: "tipo",
    path: "datiUlteriori.piscinaMare.tipo.valueId",
  },
  {
    key: "trekkingCampingAccessori",
    section: "trekkingCamping",
    field: "accessori",
    path: "datiUlteriori.trekkingCamping.accessori.valueId",
  },
  {
    key: "arrampicataTipoAttrezzatura",
    section: "arrampicata",
    field: "tipoAttrezzatura",
    path: "datiUlteriori.arrampicata.tipoAttrezzatura.valueId",
  },
  {
    key: "golfTipoAccessorioGolf",
    section: "golf",
    field: "tipoAccessorioGolf",
    path: "datiUlteriori.golf.tipoAccessorioGolf.valueId",
  },
];

const normalizeModaLookupRecords = (payload) => {
  const list = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  return list
    .map((item) => {
      if (item == null) return null;
      if (typeof item === "string") {
        const value = item.trim();
        return value ? { code: value, description: value } : null;
      }
      const code =
        item.code ??
        item.codice ??
        item.cod ??
        item.id ??
        item.value ??
        item.key ??
        item.ID;
      if (code === undefined || code === null || String(code).trim() === "") {
        return null;
      }
      const description =
        item.description ??
        item.descrizione ??
        item.label ??
        item.nomeDisplay ??
        item.nome_display ??
        item.nome ??
        item.name ??
        item.desc ??
        code;
      return {
        code: String(code).trim(),
        description: String(description ?? code).trim(),
      };
    })
    .filter(Boolean);
};

const CATEGORY_MAPPING_FILTERS_FIELD = "webCategoryMappingFilters";

const hasPersistedCategoryMappingFilters = (product) => {
  const filters = product?.[CATEGORY_MAPPING_FILTERS_FIELD];
  return Boolean(
    filters &&
    typeof filters === "object" &&
    !Array.isArray(filters) &&
    Object.keys(filters).length,
  );
};

const buildCategoryMappingSensitiveState = (modaArticolo, product) => ({
  modaValues: CATEGORY_MAPPING_MODA_FIELDS.reduce((acc, field) => {
    acc[field.key] = getFirstFilledValue(
      modaArticolo?.[field.key],
      modaArticolo?.[field.key.toUpperCase()],
    );
    return acc;
  }, {}),
  modaPending: modaArticolo?.moda_correzioni_pending ?? null,
  // Senza articolo Moda caricato dall'API aggiornata, baseline e coda non sono
  // affidabili: un modaOverrides vuoto ritirerebbe richieste ancora valide.
  modaCorrectionsAvailable: Boolean(
    modaArticolo &&
      Object.prototype.hasOwnProperty.call(modaArticolo, "moda_correzioni_pending"),
  ),
  mongoValues: CATEGORY_MAPPING_MONGO_FIELDS.reduce((acc, field) => {
    acc[field.key] = getFirstFilledValue(_.get(product, field.path));
    return acc;
  }, {}),
  persistedFilters: hasPersistedCategoryMappingFilters(product)
    ? product[CATEGORY_MAPPING_FILTERS_FIELD]
    : null,
});

const buildCategoryMappingInitialFilters = ({
  modaValues = {},
  mongoValues = {},
}) => {
  const filters = CATEGORY_MAPPING_MODA_FIELDS.reduce((acc, field) => {
    const value = getFirstFilledValue(modaValues[field.key]);
    acc[`${field.key}_in`] = value ? [value] : [];
    acc[`${field.key}_not_in`] = [];
    acc[`${field.key}_eq`] = "";
    return acc;
  }, {});
  filters.mongoFilters = CATEGORY_MAPPING_MONGO_FIELDS.reduce((acc, field) => {
    const value = getFirstFilledValue(mongoValues[field.key]);
    acc[`${field.key}_in`] = value ? [String(value)] : [];
    acc[`${field.key}_not_in`] = [];
    return acc;
  }, {});
  return filters;
};

const buildCategoryMappingConditions = (filters) => {
  const conditions = [];
  CATEGORY_MAPPING_MODA_FIELDS.forEach((field) => {
    const key = field.key;
    const eqValue = getFirstFilledValue(filters?.[`${key}_eq`]);
    const inValues = Array.isArray(filters?.[`${key}_in`])
      ? filters[`${key}_in`].filter((value) => getFirstFilledValue(value))
      : [];
    const notInValues = Array.isArray(filters?.[`${key}_not_in`])
      ? filters[`${key}_not_in`].filter((value) => getFirstFilledValue(value))
      : [];
    if (eqValue) {
      conditions.push({
        source: "moda",
        field: key,
        operator: "eq",
        value: eqValue,
      });
    } else if (notInValues.length) {
      conditions.push({
        source: "moda",
        field: key,
        operator: "not_in",
        values: notInValues,
      });
    } else if (inValues.length) {
      conditions.push({
        source: "moda",
        field: key,
        operator: "in",
        values: inValues,
      });
    }
  });
  CATEGORY_MAPPING_MONGO_FIELDS.forEach((field) => {
    const key = field.key;
    const inValues = Array.isArray(filters?.mongoFilters?.[`${key}_in`])
      ? filters.mongoFilters[`${key}_in`].filter((value) =>
          getFirstFilledValue(value),
        )
      : [];
    const notInValues = Array.isArray(filters?.mongoFilters?.[`${key}_not_in`])
      ? filters.mongoFilters[`${key}_not_in`].filter((value) =>
          getFirstFilledValue(value),
        )
      : [];
    if (notInValues.length) {
      conditions.push({
        source: "mongo",
        path: field.path,
        operator: "not_in",
        values: notInValues,
      });
    } else if (inValues.length) {
      conditions.push({
        source: "mongo",
        path: field.path,
        operator: "in",
        values: inValues,
      });
    }
  });
  return conditions;
};

const firstPositiveFilterValue = (filters, key) => {
  const eqValue = getFirstFilledValue(filters?.[`${key}_eq`]);
  if (eqValue) return eqValue;
  const inValues = Array.isArray(filters?.[`${key}_in`])
    ? filters[`${key}_in`].filter((value) => getFirstFilledValue(value))
    : [];
  return inValues.length ? inValues[0] : "";
};

const normalizeModaMappingValue = (value) =>
  String(value ?? "").trim().toUpperCase();

const buildModaOverrides = (filters, modaValues) =>
  CATEGORY_MAPPING_MODA_FIELDS.reduce((overrides, { key }) => {
    const value = firstPositiveFilterValue(filters, key);
    const baseline = getFirstFilledValue(modaValues[key]);
    if (value && normalizeModaMappingValue(value) !== normalizeModaMappingValue(baseline)) {
      overrides[key] = { value, baseline };
    }
    return overrides;
  }, {});

const restoreCategoryMappingFilters = (state, baseFilters) => {
  const persisted = state.persistedFilters;
  if (!persisted) return baseFilters;
  if (!state.modaCorrectionsAvailable) return persisted;
  const { modaOverrides, ...filters } = persisted;
  const isNewFormat = Object.prototype.hasOwnProperty.call(persisted, "modaOverrides");
  CATEGORY_MAPPING_MODA_FIELDS.forEach(({ key }) => {
    const current = normalizeModaMappingValue(state.modaValues[key]);
    let value = "";
    if (isNewFormat) {
      const override = modaOverrides?.[key];
      if (
        typeof override?.value === "string" && typeof override?.baseline === "string" &&
        normalizeModaMappingValue(override.baseline) === current
      ) {
        value = override.value.trim();
      }
    } else {
      // Una fotografia legacy non prova l'intenzione dell'utente: serve la coda API.
      const legacyValue = firstPositiveFilterValue(persisted, key);
      if (normalizeModaMappingValue(legacyValue) === normalizeModaMappingValue(state.modaPending?.[key])) {
        value = legacyValue;
      }
    }
    filters[key] = "";
    filters[`${key}_eq`] = value && normalizeModaMappingValue(value) !== current ? value : "";
    filters[`${key}_in`] = filters[`${key}_eq`] ? [] : baseFilters[`${key}_in`];
    filters[`${key}_not_in`] = [];
  });
  return filters;
};

const buildCategoryMappingResolveInput = (filters) => {
  const moda = {};
  const mongo = {};
  CATEGORY_MAPPING_MODA_FIELDS.forEach((field) => {
    const value = firstPositiveFilterValue(filters, field.key);
    if (value) {
      _.set(moda, field.key, value);
    }
  });
  CATEGORY_MAPPING_MONGO_FIELDS.forEach((field) => {
    const value = firstPositiveFilterValue(
      filters?.mongoFilters || {},
      field.key,
    );
    if (value) {
      _.set(mongo, field.path, value);
    }
  });
  return { sites: ["VIG", "BOT"], moda, mongo };
};

const buildInitialCategoryMappingOutputs = (product) => ({
  VIG: Array.isArray(product?.categorieWebAssociate)
    ? product.categorieWebAssociate.map(String)
    : [],
  BOT: Array.isArray(product?.categorieWebBotteroAssociate)
    ? product.categorieWebBotteroAssociate.map(String)
    : [],
});

const buildInitialCategoryMappingDefaultOutputs = (product) => ({
  VIG:
    product?.categoriaPredefinita === undefined ||
    product?.categoriaPredefinita === null
      ? ""
      : String(product.categoriaPredefinita),
  BOT:
    product?.categoriaBotteroPredefinita === undefined ||
    product?.categoriaBotteroPredefinita === null
      ? ""
      : String(product.categoriaBotteroPredefinita),
});

const resolveDatiUlterioriDesc = (
  datiUlterioriStandard,
  fieldConfig,
  valueId,
) => {
  const values =
    datiUlterioriStandard?.[fieldConfig.section]?.[fieldConfig.field]?.dati;
  if (!Array.isArray(values)) {
    return String(valueId);
  }
  const match = values.find(
    (item) => String(item?.idPS ?? "") === String(valueId),
  );
  return String(match?.desc ?? valueId);
};

const applySingleMongoMappingFilterToProduct = (
  product,
  filters,
  datiUlterioriStandard,
) => {
  let nextProduct = product;
  const mongoFilters = filters?.mongoFilters ?? {};
  CATEGORY_MAPPING_MONGO_FIELDS.forEach((fieldConfig) => {
    const eqValue = getFirstFilledValue(mongoFilters[fieldConfig.key + "_eq"]);
    const includeValues = Array.isArray(mongoFilters[`${fieldConfig.key}_in`])
      ? mongoFilters[`${fieldConfig.key}_in`].filter((value) =>
          getFirstFilledValue(value),
        )
      : [];
    const excludeValues = Array.isArray(
      mongoFilters[`${fieldConfig.key}_not_in`],
    )
      ? mongoFilters[`${fieldConfig.key}_not_in`].filter((value) =>
          getFirstFilledValue(value),
        )
      : [];

    const value =
      eqValue || (includeValues.length === 1 ? includeValues[0] : "");
    if (!value || excludeValues.length) {
      return;
    }

    const valueId = String(value);
    const valueDesc = resolveDatiUlterioriDesc(
      datiUlterioriStandard,
      fieldConfig,
      valueId,
    );
    nextProduct = _.set(nextProduct, fieldConfig.path, valueId);
    nextProduct = _.set(
      nextProduct,
      fieldConfig.path.replace(/\.valueId$/, ".valueDesc"),
      valueDesc,
    );
  });
  return nextProduct;
};

const CATEGORY_PRODUCT_SITE_FIELDS = {
  VIG: {
    label: "VIG",
    categoriesField: "categorieWebAssociate",
    defaultField: "categoriaPredefinita",
  },
  BOT: {
    label: "BOT",
    categoriesField: "categorieWebBotteroAssociate",
    defaultField: "categoriaBotteroPredefinita",
  },
};

const formatReferenceDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPubblicatoStateFromProduct = (product) => {
  const rawValueId = _.get(product, "datiUlteriori.web.pubblicato.valueId");
  const normalizedId =
    rawValueId === undefined || rawValueId === null
      ? null
      : String(rawValueId).trim() || null;
  const normalizedLabel = publicationLabelFromValue(
    _.get(product, "datiUlteriori.web.pubblicato"),
  );
  return { valueId: normalizedId, label: normalizedLabel };
};

const useDatiUlterioriStandard = (token) => {
  const [datiUlterioriStandard, setDatiUlterioriStandard] = useState({});

  useEffect(() => {
    if (!token) {
      setDatiUlterioriStandard({});
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    fetch(apiUrl + "/prodotti/get/datiUlteriori", {
      headers: { Authorization: "Bearer " + token },
      signal: controller.signal,
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!cancelled) {
          setDatiUlterioriStandard(data);
        }
      })
      .catch((error) => {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        console.error("Errore nel recupero dei dati ulteriori:", error);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  return datiUlterioriStandard;
};

const useModaArticoloData = (codVS, token) => {
  const [modaArticolo, setModaArticolo] = useState(null);
  const [modaArticoloLoading, setModaArticoloLoading] = useState(false);
  const [modaArticoloError, setModaArticoloError] = useState(null);

  useEffect(() => {
    setModaArticolo(null);
    setModaArticoloError(null);
    setModaArticoloLoading(false);
  }, [codVS]);

  useEffect(() => {
    if (!codVS || !token) {
      setModaArticolo(null);
      setModaArticoloError(null);
      setModaArticoloLoading(false);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    const loadModaArticolo = async () => {
      setModaArticoloLoading(true);
      setModaArticoloError(null);
      try {
        const response = await fetch(
          `${apiUrl}/moda/articoli/${encodeURIComponent(codVS)}`,
          {
            headers: {
              Authorization: "Bearer " + token,
            },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const message =
            payload?.message ||
            response.statusText ||
            "Errore nel recupero dei dati MODA";
          throw new Error(message);
        }
        if (!cancelled) {
          setModaArticolo(payload?.data ?? null);
          if (payload?.success === false && payload?.message) {
            setModaArticoloError(payload.message);
          } else {
            setModaArticoloError(null);
          }
        }
      } catch (error) {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        setModaArticolo(null);
        setModaArticoloError(error.message);
      } finally {
        if (!cancelled) {
          setModaArticoloLoading(false);
        }
      }
    };

    loadModaArticolo();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [codVS, token]);

  return { modaArticolo, modaArticoloLoading, modaArticoloError };
};

const usePrestashopReportData = (codVS, token) => {
  const [psData, setPsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPsData(null);
    setLoading(false);
    setError(null);
  }, [codVS]);

  useEffect(() => {
    if (!codVS || !token) {
      setPsData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${apiUrl}/moda/articoli/${encodeURIComponent(codVS)}/prestashop`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const message =
            payload?.error ??
            payload?.message ??
            response.statusText ??
            "Errore nel recupero dei dati Prestashop";
          throw new Error(message);
        }
        if (!cancelled) {
          setPsData(payload?.data ?? null);
        }
      } catch (err) {
        if (controller.signal.aborted || cancelled) {
          return;
        }
        setPsData(null);
        setError(err?.message ?? "Errore nel recupero dei dati Prestashop");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [codVS, token]);

  return {
    prestashopReport: psData,
    prestashopReportLoading: loading,
    prestashopReportError: error,
  };
};

const usePrestashopOnlineData = (codVS, token, enabled = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!enabled || !codVS || !token) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${apiUrl}/moda/articoli/${encodeURIComponent(codVS)}/prestashop-online`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => null);
        if (!response.ok)
          throw new Error(
            payload?.error ||
              payload?.message ||
              "Errore nel recupero dei dati online",
          );
        if (!cancelled) setData(payload?.data ?? null);
      } catch (err) {
        if (!cancelled && !controller.signal.aborted) {
          setData(null);
          setError(err?.message || "Errore nel recupero dei dati online");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [codVS, token, enabled]);
  return {
    prestashopOnline: data,
    prestashopOnlineLoading: loading,
    prestashopOnlineError: error,
  };
};

const useUiFeatureFlags = (token) => {
  const [flags, setFlags] = useState({
    loaded: false,
    nascondiSupportoMultisito: false,
  });

  useEffect(() => {
    if (!token) {
      setFlags({ loaded: false, nascondiSupportoMultisito: false });
      return undefined;
    }
    const controller = new AbortController();
    fetch(apiUrl + "/auth/ui-flags", {
      headers: { Authorization: "Bearer " + token },
      signal: controller.signal,
    })
      .then((response) =>
        response.ok
          ? response.json()
          : Promise.reject(new Error("Flag UI non disponibili")),
      )
      .then((data) =>
        setFlags({
          loaded: true,
          nascondiSupportoMultisito: data?.nascondiSupportoMultisito === true,
        }),
      )
      .catch((error) => {
        if (error?.name !== "AbortError") {
          setFlags({ loaded: true, nascondiSupportoMultisito: true });
        }
      });
    return () => controller.abort();
  }, [token]);

  return flags;
};

function ModificaProdottiNEW(props) {
  /////// PROPS ///////////////

  const setProductFunction = props.setProductFunction;
  const listaNomiProdotto = props.listaNomiProdotto;
  const utente = props.utente;
  const solaLettura = isUtenteSpeciale(utente);
  const { apriProdotto, errore: erroreApertura } = useApriProdottoSolaLettura();
  const onClose = props.onClose;
  const location = useLocation();
  const navigate = useNavigate();
  const cameFromModa = Boolean(location.state?.fromModaArticoli);
  const initialImportBotRequested = hasImportBotFlag(props.modaArticoloIniziale);
  const initialExportVigRequested = hasExportVigFlag(props.modaArticoloIniziale);
  const initialAlignmentPage = initialExportVigRequested
    ? "export-bottero"
    : initialImportBotRequested
      ? "import-bottero"
      : "categorie";

  const {
    buttonClassesWhite,
    buttonClassesRed,
    buttonClassesGreen,
    inputClasses,
    inputClassesNOW,
    inputClassesConDueTasti,
    classiTastoSblocco,
    classiTastoCopiaInAlto,
    tastoQuadratoVerde,
    tastoQuadratoGiallo,
    tastoQuadratoBianco,
    vociMenuTabs,
    vociMenuTabsSelezionata,
    riquadroClassesRed,
  } = stilePulsanti;

  const languages = ["it", "en", "fr"];
  const { user, /* isAuthenticated, */ getAccessTokenSilently } = useAuth0();

  ///// HOOKS //////////////////

  const [lang, setLang] = useState("it");
  const [pagina, setPagina] = useState(initialAlignmentPage);
  const [categoryPageView, setCategoryPageView] = useState("mapping");
  const [importBotteroMounted, setImportBotteroMounted] = useState(
    initialImportBotRequested,
  );
  const [exportBotteroMounted, setExportBotteroMounted] = useState(
    initialExportVigRequested,
  );
  const importBotteroAutoOpenedRef = useRef(initialImportBotRequested);
  const exportBotteroAutoOpenedRef = useRef(initialExportVigRequested);
  const [prodottoTemp, setProdottoTemp] = useState(props.prodotto);
  const [avvisoSpedizione, setAvvisoSpedizione] = useState(null);
  const prodottoIdentity = getProductIdentity(prodottoTemp);
  const [publicationDestinations, setPublicationDestinations] = useState(() =>
    getPublicationDestinations(props.modaArticoloIniziale),
  );
  const [publicationDestinationsDirty, setPublicationDestinationsDirty] =
    useState(false);
  const [prodottoRiferimento, setProdottoRiferimento] = useState(prodVuoto);
  const [referenceResultsModal, setReferenceResultsModal] = useState(null);
  const [referenceResultsLoading, setReferenceResultsLoading] = useState(false);
  const [referenceSearchFeedback, setReferenceSearchFeedback] = useState("");
  const [codProdModSuggerimento, setCodProdModSuggerimento] = useState("");
  const [codProdColSuggerimento, setCodProdColSuggerimento] = useState("");
  const [appuntiProdottoValue, setAppuntiProdottoValue] = useState("");
  const [token, setToken] = useState("");
  const [tab, setTab] = useState("generici");
  const [refreshImmaginiKey, setRefreshImmaginiKey] = useState(0);
  const [immagineCompletataInSessione, setImmagineCompletataInSessione] =
    useState(false);
  const [resultSalvataggio, setResultSalvataggio] = useState(false);
  const [alertSalvataggio, setAlertSalvataggio] = useState(false);
  const [attivaEditor, setAttivaEditor] = useState(false);
  const [attivaEditorIntro, setAttivaEditorIntro] = useState(false);
  const [salvato, setSalvato] = useState(true);
  const [traduzioneInCorso, setTraduzioneInCorso] = useState(false);
  const [testContentEditor, setTestContentEditor] = useState("");
  const datiUlterioriStandard = useDatiUlterioriStandard(token);
  const { modaArticolo, modaArticoloLoading, modaArticoloError } =
    useModaArticoloData(prodottoTemp?.codiceVS, token);
  const { prestashopReport, prestashopReportLoading, prestashopReportError } =
    usePrestashopReportData(prodottoTemp?.codiceVS, token);
  const uiFeatureFlags = useUiFeatureFlags(token);
  const canViewMultisiteSupport = canViewMultisiteSupportForUser(
    utente,
    uiFeatureFlags,
  );
  const { prestashopOnline, prestashopOnlineLoading, prestashopOnlineError } =
    usePrestashopOnlineData(
      prodottoTemp?.codiceVS,
      token,
      canViewMultisiteSupport,
    );

  useEffect(() => {
    const currentCodVS = String(prodottoTemp?.codiceVS ?? "").trim();
    const loadedCodVS = String(modaArticolo?.codVS ?? modaArticolo?.codiceVS ?? "").trim();
    if (!currentCodVS || loadedCodVS !== currentCodVS) {
      return;
    }
    setPublicationDestinations(getPublicationDestinations(modaArticolo));
    setPublicationDestinationsDirty(false);
  }, [modaArticolo, prodottoTemp?.codiceVS]);

  useEffect(() => {
    if (!canViewMultisiteSupport && tab === "bottero") {
      setTab("generici");
    }
  }, [canViewMultisiteSupport, tab]);
  const [categoryMappingModalOpen, setCategoryMappingModalOpen] =
    useState(false);
  const [categoryMappingSaving, setCategoryMappingSaving] = useState(false);
  const [categoryMappingFeedback, setCategoryMappingFeedback] = useState("");
  const [categoryMappingLookupOptions, setCategoryMappingLookupOptions] =
    useState({});
  const [categoryMappingSiteCategories, setCategoryMappingSiteCategories] =
    useState({
      VIG: [],
      BOT: [],
    });
  const [categoryProductMappingsResult, setCategoryProductMappingsResult] =
    useState(null);
  const [, setCategoryProductMappingsLoading] = useState(false);
  const [, setCategoryProductMappingsError] = useState("");
  const categoryMappingResolveRequestRef = useRef(0);
  const categoryMappingSensitiveSignature = JSON.stringify(
    buildCategoryMappingSensitiveState(modaArticolo, prodottoTemp),
  );
  const categoryMappingSensitiveState = useMemo(
    () => JSON.parse(categoryMappingSensitiveSignature),
    [categoryMappingSensitiveSignature],
  );
  // Il callback del modale deve restare stabile quando cambia la fotografia salvata.
  const categoryMappingModaValuesSignature = JSON.stringify(categoryMappingSensitiveState.modaValues);
  const categoryMappingModaValues = useMemo(
    () => JSON.parse(categoryMappingModaValuesSignature),
    [categoryMappingModaValuesSignature],
  );
  const categoryMappingModaCorrectionsAvailable =
    categoryMappingSensitiveState.modaCorrectionsAvailable;
  const categoryMappingBaseFilters = useMemo(
    () => buildCategoryMappingInitialFilters(categoryMappingSensitiveState),
    [categoryMappingSensitiveState],
  );
  const categoryMappingInitialFilters = useMemo(
    () =>
      restoreCategoryMappingFilters(categoryMappingSensitiveState, categoryMappingBaseFilters),
    [categoryMappingBaseFilters, categoryMappingSensitiveState],
  );
  const categoryMappingInitialOutputs = useMemo(
    () => buildInitialCategoryMappingOutputs(prodottoTemp),
    [prodottoTemp],
  );
  const categoryMappingInitialDefaultOutputs = useMemo(
    () => buildInitialCategoryMappingDefaultOutputs(prodottoTemp),
    [prodottoTemp],
  );
  const categoryMappingRequirements = useMemo(
    () =>
      ["VIG", "BOT"].flatMap((site) => {
        const requirement =
          categoryProductMappingsResult?.[site]?.mappingRequirement;
        return requirement?.requiresInput || requirement?.requiresReview
          ? [{ site, ...requirement }]
          : [];
      }),
    [categoryProductMappingsResult],
  );
  const categoryMappingInitialTab = useMemo(
    () =>
      categoryMappingRequirements.some((requirement) =>
        requirement.missingRequirements?.some(
          (item) => item.source === "mongo",
        ),
      )
        ? "web"
        : "moda",
    [categoryMappingRequirements],
  );
  const categoryMappingAuthHeaders = useMemo(
    () => ({
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
    }),
    [token],
  );
  const resolveExistingCategoryMappings = useCallback(
    async (filters) => {
      if (!token) {
        throw new Error("Token non ancora disponibile.");
      }
      const response = await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules/resolve",
        buildCategoryMappingResolveInput(filters),
        categoryMappingAuthHeaders,
      );
      return response?.data?.data || {};
    },
    [categoryMappingAuthHeaders, token],
  );

  const reloadProductCategoryMappings = useCallback(async () => {
    const requestId = categoryMappingResolveRequestRef.current + 1;
    categoryMappingResolveRequestRef.current = requestId;
    if (!token) {
      setCategoryProductMappingsResult(null);
      setCategoryProductMappingsLoading(false);
      return;
    }
    setCategoryProductMappingsLoading(true);
    setCategoryProductMappingsError("");
    try {
      const result = await resolveExistingCategoryMappings(
        categoryMappingInitialFilters,
      );
      if (requestId !== categoryMappingResolveRequestRef.current) return;
      setCategoryProductMappingsResult(result || {});
    } catch (error) {
      if (requestId !== categoryMappingResolveRequestRef.current) return;
      setCategoryProductMappingsResult(null);
      setCategoryProductMappingsError(
        error?.message || "Errore caricamento map categorie applicabili.",
      );
    } finally {
      if (requestId === categoryMappingResolveRequestRef.current) {
        setCategoryProductMappingsLoading(false);
      }
    }
  }, [categoryMappingInitialFilters, resolveExistingCategoryMappings, token]);

  const lastProductIdentityRef = useRef(null);
  const pubblicatoInitialRef = useRef(
    getPubblicatoStateFromProduct(prodottoTemp).label,
  );
  const pubblicatoLastSavedRef = useRef(undefined);
  const pubblicatoValueId = _.get(
    prodottoTemp,
    "datiUlteriori.web.pubblicato.valueId",
  );
  const pubblicatoValueDesc = _.get(
    prodottoTemp,
    "datiUlteriori.web.pubblicato.valueDesc",
  );
  const auditUsername = useMemo(
    () =>
      typeof utente?.username === "string" ? utente.username.trim() : "",
    [utente?.username],
  );

  const goBackToModa = useCallback(() => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }
    if (cameFromModa) {
      navigate("/uiPubblicazione", {
        replace: true,
      });
    }
  }, [cameFromModa, navigate, onClose]);

  // Se la route ha passato un prodotto nello state (navigate(..., { state: { prodotto } })),
  // inizializziamo immediatamente lo stato locale del prodotto per evitare problemi di timing.
  useEffect(() => {
    const prodottoDaLocation = location.state?.prodotto;
    if (prodottoDaLocation) {
      console.log(
        "ModificaProdottiNEW: inizializzo prodottoTemp da location.state.prodotto",
        prodottoDaLocation,
      );
      setProdottoTemp(prodottoDaLocation);
    }
  }, [location.state?.prodotto]);

  useEffect(() => {
    setAvvisoSpedizione(null);
  }, [prodottoIdentity]);

  useEffect(() => {
    if (lastProductIdentityRef.current !== prodottoIdentity) {
      lastProductIdentityRef.current = prodottoIdentity;
      pubblicatoInitialRef.current =
        getPubblicatoStateFromProduct(prodottoTemp).label;
      pubblicatoLastSavedRef.current = undefined;
    }
  }, [prodottoIdentity, pubblicatoValueId, pubblicatoValueDesc]);

  useEffect(() => {
    if (prodottoTemp?.codiceProduttore?.codProduttoreIntero) {
      codiceProduttoreAutoSplitter(
        prodottoTemp.codiceProduttore.codProduttoreIntero,
      );
    }
  }, [prodottoTemp.codiceProduttore]);

  useEffect(() => {
    if (!token) {
      setCategoryMappingLookupOptions({});
      return;
    }
    let cancelled = false;
    const loadLookups = async () => {
      try {
        const entries = await Promise.all(
          CATEGORY_MAPPING_MODA_FIELDS.map(async (field) => {
            const response = await fetch(apiUrl + field.lookupUrl, {
              headers: { Authorization: "Bearer " + token },
            });
            const payload = await response.json().catch(() => null);
            if (!response.ok) {
              throw new Error(payload?.message || response.statusText);
            }
            return [field.key, normalizeModaLookupRecords(payload)];
          }),
        );
        if (!cancelled) {
          setCategoryMappingLookupOptions(Object.fromEntries(entries));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Errore caricamento lookup mapping categorie:", error);
          setCategoryMappingLookupOptions({});
        }
      }
    };
    loadLookups();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      setCategoryMappingSiteCategories({ VIG: [], BOT: [] });
      return;
    }
    let cancelled = false;
    const loadSiteCategories = async () => {
      try {
        const [vigResponse, botResponse] = await Promise.all([
          axios.get(
            apiUrl + "/prodotti/categories/site-categories?site=VIG",
            categoryMappingAuthHeaders,
          ),
          axios.get(
            apiUrl + "/prodotti/categories/site-categories?site=BOT",
            categoryMappingAuthHeaders,
          ),
        ]);
        if (!cancelled) {
          setCategoryMappingSiteCategories({
            VIG: Array.isArray(vigResponse?.data?.data)
              ? vigResponse.data.data
              : [],
            BOT: Array.isArray(botResponse?.data?.data)
              ? botResponse.data.data
              : [],
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Errore caricamento categorie sito mapping:", error);
          setCategoryMappingSiteCategories({ VIG: [], BOT: [] });
        }
      }
    };
    loadSiteCategories();
    return () => {
      cancelled = true;
    };
  }, [categoryMappingAuthHeaders, token]);

  const handleUploadImmaginiSuccess = (payload) => {
    setRefreshImmaginiKey((prev) => prev + 1);
    if (["images-uploaded", "image-present"].includes(payload?.action)) {
      setImmagineCompletataInSessione(true);
    }
  };

  const handleBeforeAlignmentApply = useCallback(() => {
    if (salvato) return true;
    setAlertSalvataggio(
      "Salva prima le modifiche locali al prodotto: il flusso aggiorna direttamente Domino.",
    );
    return false;
  }, [salvato]);

  const reloadProductAfterAlignment = useCallback(async () => {
    if (!token || !prodottoTemp?.codiceVS) return false;
    try {
      const response = await axios.post(
        apiUrl + "/prodotti/prodotto/showORcreateIDPS4",
        {
          codiceVS: prodottoTemp.codiceVS,
          sorgenteModa: modaArticolo ?? props.modaArticoloIniziale ?? null,
        },
        { headers: { Authorization: "Bearer " + token } },
      );
      const products = Array.isArray(response?.data) ? response.data : [];
      const refreshedProduct = products[0];
      if (!refreshedProduct) throw new Error("Documento Mongo non restituito.");
      setProdottoTemp(refreshedProduct);
      if (typeof setProductFunction === "function") {
        setProductFunction(refreshedProduct);
      }
      setSalvato(true);
      setAlertSalvataggio(false);
      return true;
    } catch (error) {
      console.error("Import applicato ma prodotto non ricaricato:", error);
      setAlertSalvataggio(
        "Import applicato su Domino, ma la scheda locale non è stata ricaricata. Chiudi e riapri il prodotto prima di modificarlo.",
      );
      return false;
    }
  }, [modaArticolo, prodottoTemp?.codiceVS, props.modaArticoloIniziale, setProductFunction, token]);

  useEffect(() => {
    setImmagineCompletataInSessione(false);
  }, [prodottoTemp?.codiceVS]);

  const handleCategoryMappingFiltersChange = useCallback(
    (filters) => {
      const normalizedFilters =
        filters && typeof filters === "object" && !Array.isArray(filters)
          ? filters
          : {};
      const persistedFilters = categoryMappingModaCorrectionsAvailable
        ? {
            ...normalizedFilters,
            modaOverrides: buildModaOverrides(normalizedFilters, categoryMappingModaValues),
          }
        : normalizedFilters;
      setSalvato(false);
      setProdottoTemp((prevProduct) => {
        let nextProduct = _.set(
          _.cloneDeep(prevProduct),
          CATEGORY_MAPPING_FILTERS_FIELD,
          persistedFilters,
        );
        nextProduct = applySingleMongoMappingFilterToProduct(
          nextProduct,
          normalizedFilters,
          datiUlterioriStandard,
        );
        return nextProduct;
      });
    },
    [datiUlterioriStandard, categoryMappingModaValues, categoryMappingModaCorrectionsAvailable],
  );

  const handleApplyCategoryMappingFromFilters = async (payload) => {
    // Il modale in modalita mappatura attende il salvataggio senza chiudersi: qui
    // l'esito si mostra nella pagina, come prima.
    setCategoryMappingModalOpen(false);
    const mappingUsername = getFirstFilledValue(utente?.username);
    if (!mappingUsername.includes("@")) {
      setCategoryMappingFeedback(
        "Username utente mancante o non valido: aggiorna la sessione prima di salvare la mappatura.",
      );
      return;
    }
    if (!token) {
      setCategoryMappingFeedback("Token non ancora disponibile.");
      return;
    }
    if (!utente?.permessiAPI?.["update:prestashop"]) {
      setCategoryMappingFeedback(
        "Permesso update:prestashop necessario per salvare la mappatura.",
      );
      return;
    }
    const filters = payload?.filters || {};
    const mappingName = getFirstFilledValue(payload?.categoryMappingName);
    const categoryOutputs = payload?.categoryOutputs || {};
    const categoryDefaultOutputs = payload?.categoryDefaultOutputs || {};
    const conditions = buildCategoryMappingConditions(filters);
    const outputs = ["VIG", "BOT"].flatMap((site) => {
      const siteOutputs = Array.isArray(categoryOutputs?.[site])
        ? categoryOutputs[site]
        : [];
      const defaultOutput = categoryDefaultOutputs?.[site] || "";
      return siteOutputs.map((externalCategoryId, index) => ({
        site,
        externalCategoryId,
        isDefaultCandidate:
          String(externalCategoryId) === String(defaultOutput),
        priority: index,
      }));
    });
    if (!conditions.length) {
      setCategoryMappingFeedback(
        "Seleziona almeno un requisito MODA per creare la regola.",
      );
      return;
    }
    if (!outputs.length) {
      setCategoryMappingFeedback(
        "Seleziona almeno una categoria VIG o BOT come output.",
      );
      return;
    }
    setCategoryMappingSaving(true);
    setCategoryMappingFeedback("");
    try {
      await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules",
        {
          username: mappingUsername,
          ruleKey: "",
          description:
            mappingName ||
            "Mapping da filtri MODA prodotto " + (prodottoTemp?.codiceVS || ""),
          priority: 0,
          active: true,
          isDefaultCandidate: false,
          conditionsJson: JSON.stringify(conditions, null, 2),
          outputs,
        },
        categoryMappingAuthHeaders,
      );
      setCategoryMappingFeedback("Mappatura categorie salvata.");
      await reloadProductCategoryMappings();
    } catch (error) {
      setCategoryMappingFeedback(
        error?.response?.data?.error ||
          error?.message ||
          "Errore salvataggio mappatura categorie.",
      );
    } finally {
      setCategoryMappingSaving(false);
    }
  };

  const buildSavePayloadExtras = () => {
    const normalizedCurrent = getPubblicatoStateFromProduct(prodottoTemp).label;
    const initialValue = pubblicatoInitialRef.current;
    const articoloSql = modaArticolo ?? props.modaArticoloIniziale;
    const hasSqlStatus = articoloSql &&
      Object.prototype.hasOwnProperty.call(articoloSql, "pubblicato");
    const sqlStatus = hasSqlStatus
      ? String(articoloSql.pubblicato ?? "").trim().toUpperCase() || null
      : null;
    // Mongo puo essere gia SI mentre la lista SQL conserva NO. Dopo un
    // salvataggio riuscito, il dettaglio SQL caricato all'apertura e obsoleto.
    const shouldAlignSql = pubblicatoLastSavedRef.current === undefined &&
      hasSqlStatus && normalizedCurrent !== sqlStatus;
    const shouldPromotePubblicato =
      normalizedCurrent !== null &&
      (normalizedCurrent !== initialValue || shouldAlignSql);
    const extraFlags = {};
    if (shouldPromotePubblicato) {
      extraFlags.pubblicato = true;
    }
    return { extraFlags, normalizedCurrent, shouldPromotePubblicato };
  };

  const getCategoriaPredefinitaValidationError = useCallback(
    (documento, siteKey = "VIG") => {
      const config = CATEGORY_PRODUCT_SITE_FIELDS[siteKey];
      if (!config) return null;
      const categorieSelezionateRaw = Array.isArray(
        _.get(documento, config.categoriesField),
      )
        ? _.get(documento, config.categoriesField)
        : [];
      const categorieSelezionate = categorieSelezionateRaw
        .map((item) => normalizeCategoryId(item))
        .filter((item) => item !== null);

      if (!categorieSelezionate.length) {
        return null;
      }

      const categoriaPredefinita = normalizeCategoryId(
        _.get(documento, config.defaultField),
      );
      if (categoriaPredefinita === null) {
        return `Seleziona la categoria web predefinita ${config.label} prima di salvare.`;
      }

      if (!categorieSelezionate.includes(categoriaPredefinita)) {
        return `La categoria web predefinita ${config.label} deve essere una delle categorie selezionate nell'albero.`;
      }

      return null;
    },
    [],
  );

  const handlePostSaveSideEffects = (extraFlags, normalizedCurrent) => {
    if (extraFlags.pubblicato) {
      pubblicatoInitialRef.current = normalizedCurrent;
      pubblicatoLastSavedRef.current = normalizedCurrent;
    }
  };

  useEffect(() => {
    const getUserMetadata = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        setToken(accessToken);
      } catch (e) {
        console.log(e.message);
      }
    };
    getUserMetadata();
  }, [getAccessTokenSilently, user?.sub]);

  useEffect(() => {
    setAttivaEditor(false);
    setAttivaEditorIntro(false);
    setCategoryProductMappingsResult(null);
    setCategoryProductMappingsError("");
  }, [prodottoTemp?.codiceVS]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      reloadProductCategoryMappings();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [reloadProductCategoryMappings]);

  useEffect(() => {
    importBotteroAutoOpenedRef.current = initialImportBotRequested;
    exportBotteroAutoOpenedRef.current = initialExportVigRequested;
    setImportBotteroMounted(initialImportBotRequested);
    setExportBotteroMounted(initialExportVigRequested);
    setCategoryPageView("mapping");
    setPagina(initialExportVigRequested
      ? "export-bottero"
      : initialImportBotRequested
        ? "import-bottero"
        : "categorie");
  }, [
    prodottoTemp?.codiceVS,
    initialImportBotRequested,
    initialExportVigRequested,
  ]);

  useEffect(() => {
    const gateResolved = Boolean(utente?.permessiAPI?.["read:debug"]) || uiFeatureFlags.loaded;
    if (!gateResolved) return;
    const importEligible = canViewMultisiteSupport && normalizeTinyIntFlag(
      modaArticolo?.mostraImportBot ??
        modaArticolo?.mostra_import_bot ??
        props.modaArticoloIniziale?.mostraImportBot ??
        props.modaArticoloIniziale?.mostra_import_bot,
    ) === 1;
    const exportEligible = canViewMultisiteSupport && normalizeTinyIntFlag(
      modaArticolo?.mostraExportVig ??
        modaArticolo?.mostra_export_vig ??
        props.modaArticoloIniziale?.mostraExportVig ??
        props.modaArticoloIniziale?.mostra_export_vig,
    ) === 1;
    const preferredPage = exportEligible
      ? "export-bottero"
      : importEligible
        ? "import-bottero"
        : null;
    const preferredAlreadyOpened = preferredPage === "export-bottero"
      ? exportBotteroAutoOpenedRef.current
      : preferredPage === "import-bottero"
        ? importBotteroAutoOpenedRef.current
        : false;

    if (preferredPage && !preferredAlreadyOpened) {
      importBotteroAutoOpenedRef.current = importEligible;
      exportBotteroAutoOpenedRef.current = exportEligible;
      if (preferredPage === "export-bottero") setExportBotteroMounted(true);
      if (preferredPage === "import-bottero") setImportBotteroMounted(true);
      setPagina(preferredPage);
      return;
    }
    if (
      !canViewMultisiteSupport &&
      (pagina === "import-bottero" || pagina === "export-bottero")
    ) {
      setPagina("categorie");
    }
  }, [
    canViewMultisiteSupport,
    modaArticolo?.mostraImportBot,
    modaArticolo?.mostra_import_bot,
    modaArticolo?.mostraExportVig,
    modaArticolo?.mostra_export_vig,
    pagina,
    props.modaArticoloIniziale?.mostraImportBot,
    props.modaArticoloIniziale?.mostra_import_bot,
    props.modaArticoloIniziale?.mostraExportVig,
    props.modaArticoloIniziale?.mostra_export_vig,
    uiFeatureFlags.loaded,
    utente?.permessiAPI,
  ]);

  const mostraImportBot = normalizeTinyIntFlag(
    modaArticolo?.mostraImportBot ??
      modaArticolo?.mostra_import_bot ??
      props.modaArticoloIniziale?.mostraImportBot ??
      props.modaArticoloIniziale?.mostra_import_bot,
  ) === 1;
  const mostraExportVig = normalizeTinyIntFlag(
    modaArticolo?.mostraExportVig ??
      modaArticolo?.mostra_export_vig ??
      props.modaArticoloIniziale?.mostraExportVig ??
      props.modaArticoloIniziale?.mostra_export_vig,
  ) === 1;
  const canShowImportBot = canViewMultisiteSupport;
  const canShowExportVig = canViewMultisiteSupport;

  const modaHiddenKeys = new Set([
    "codvs",
    "rawpayload",
    "raw_payload",
    "hash",
    "created_at",
    "updated_at",
  ]);
  const modaDateKeys = new Set([
    "created_at",
    "updated_at",
    "source_updated_at",
    "ps_updated_at",
  ]);
  const modaCustomLabels = {
    source_updated_at: "Ultima modifica in Moda",
    codprod: "Codice produttore",
    nome_colore: "Nome colore",
  };

  const formatModaLabel = (key) => {
    const keyLower = key.toLowerCase();
    if (modaCustomLabels[keyLower]) {
      return modaCustomLabels[keyLower];
    }
    return key
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map((word) =>
        word.length > 0
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : word,
      )
      .join(" ");
  };

  const formatModaValue = (keyLower, value) => {
    if (value === undefined || value === null || value === "") {
      return "N/A";
    }
    if (modaDateKeys.has(keyLower)) {
      try {
        return umanizzaDataSenzaSecondi(value);
      } catch (error) {
        return String(value);
      }
    }
    return String(value);
  };

  const modaAnnullato = modaArticolo && Number(modaArticolo.annullato) === 1;
  const modaBrandCode = modaArticolo?.brandCode ?? modaArticolo?.brand ?? null;
  const modaBrandDescription =
    modaArticolo?.brandDescription ?? modaArticolo?.brand_description ?? null;
  const modaBrandNoteRaw =
    modaArticolo?.brandNote ?? modaArticolo?.brand_note ?? null;
  const modaBrandNote =
    typeof modaBrandNoteRaw === "string" && modaBrandNoteRaw.trim().length > 0
      ? modaBrandNoteRaw.trim()
      : null;
  const modaCodProd =
    modaArticolo?.codProd ?? modaArticolo?.codprod ?? modaArticolo?.cod_prod;
  const modaCodMod =
    modaArticolo?.codMod ?? modaArticolo?.codmod ?? modaArticolo?.cod_mod;
  const modaCodCol =
    modaArticolo?.codCol ?? modaArticolo?.codcol ?? modaArticolo?.cod_col;
  const modaNome = modaArticolo?.nome ?? null;
  const modaNomeColore =
    modaArticolo?.nome_colore ?? modaArticolo?.nomeColore ?? null;
  const brandSitoDisabled =
    normalizeTinyIntFlag(
      modaArticolo?.permessoSito ?? modaArticolo?.permesso_sito,
      1,
    ) === 0;
  const brandRestrictionLabels = [];
  if (!brandSitoDisabled) {
    if (
      normalizeTinyIntFlag(
        modaArticolo?.permessoMp ?? modaArticolo?.permesso_mp,
        1,
      ) === 0
    ) {
      brandRestrictionLabels.push("No marketplace");
    }
    if (
      normalizeTinyIntFlag(
        modaArticolo?.permessoEstero ?? modaArticolo?.permesso_estero,
        1,
      ) === 0
    ) {
      brandRestrictionLabels.push("No permessi estero");
    }
    if (
      normalizeTinyIntFlag(
        modaArticolo?.dropshipping ?? modaArticolo?.permessoDropshipping,
        0,
      ) === 1
    ) {
      brandRestrictionLabels.push("Sì dropshipping");
    }
    if (
      normalizeTinyIntFlag(
        modaArticolo?.inventorySharing ?? modaArticolo?.inventory_sharing,
        0,
      ) === 1
    ) {
      brandRestrictionLabels.push("Sì inventory sharing");
    }
  }
  const brandLimitazioniAlert =
    brandSitoDisabled || brandRestrictionLabels.length > 0
      ? {
          brandLabel: pickFirstNonEmptyString(
            modaBrandDescription,
            modaBrandCode,
          ),
          messages: brandSitoDisabled
            ? [
                "Permesso sito: NO. Questo brand non va considerato per la pubblicazione web.",
              ]
            : brandRestrictionLabels,
        }
      : null;

  ///// FUNZIONI ///////////////

  const handleChange = async function (evt) {
    setSalvato(false);
    const value = evt.target.value;
    try {
      if (evt.target.name === "codiceVS") {
        //CONTROLLARE SE QUESTO SERVE ANCORA, IMMAGINO DI NO
        setProdottoTemp({
          ...prodottoTemp,
          _id: "",
        });
        setCodProdModSuggerimento("");
        setCodProdColSuggerimento("");
      }
    } catch (err) {
      console.log(
        "Errore nella rimozione dell'id del prodotto corrente (come precauzione per sovrascritture involontarie): " +
          err,
      );
      throw err;
    }
    try {
      if (evt.target.name === "codiceProduttore.codProduttoreIntero") {
        codiceProduttoreAutoSplitter(evt.target.value);
      }
    } catch (err) {
      console.log("Errore nell'autosplittamento: " + err);
      throw err;
    }
    try {
      if (evt.target.name.includes(".")) {
        /* console.log("path tipo: " + evt.target.name) */
        const update = _.set(_.cloneDeep(prodottoTemp), evt.target.name, value); // USO QUESTO METODO INVECE DI QUELLO SOPRA MA E' RESOURCE EXPENSIVE
        setProdottoTemp(update);
      }
    } catch (err) {
      console.log("Errore nell'aggiornamento di un dato nestato: " + err);
      throw err;
    }
    try {
      if (!evt.target.name.includes(".")) {
        setProdottoTemp({
          ...prodottoTemp,
          [evt.target.name]: value,
        });
      }
    } catch (err) {
      console.log("Errore nell'aggiornamento di un dato non nestato: " + err);
      throw err;
    }
  };

  const cercaIdColore = function (nomeCol, numIdPs) {
    const colore = datiUlterioriStandard.generici.colori.dati.find(
      (el) => el["desc"] === nomeCol,
    );
    return colore[numIdPs];
  };

  const handleChangeColoreNonSel = async function (evt) {
    const name = evt.target.getAttribute("data-name");
    setSalvato(false);
    try {
      if (
        !prodottoTemp?.datiUlteriori?.generici?.colore1?.valueId ||
        prodottoTemp?.datiUlteriori?.generici?.colore1?.valueId === ""
      ) {
        const value = cercaIdColore(name, "idPS1");
        const update = _.set(
          _.cloneDeep(prodottoTemp),
          "datiUlteriori.generici.colore1",
          {
            ...prodottoTemp.datiUlteriori.generici.colore1,
            valueId: value,
            valueDesc: name,
          },
        );
        setProdottoTemp(update);
      } else if (
        !prodottoTemp?.datiUlteriori?.generici?.colore2?.valueId ||
        prodottoTemp?.datiUlteriori?.generici?.colore2?.valueId === ""
      ) {
        const value = cercaIdColore(name, "idPS2");
        const update = _.set(
          _.cloneDeep(prodottoTemp),
          "datiUlteriori.generici.colore2",
          {
            ...prodottoTemp.datiUlteriori.generici.colore2,
            valueId: value,
            valueDesc: name,
          },
        );
        setProdottoTemp(update);
      } else if (
        !prodottoTemp?.datiUlteriori?.generici?.colore3?.valueId ||
        prodottoTemp?.datiUlteriori?.generici?.colore3?.valueId === ""
      ) {
        const value = cercaIdColore(name, "idPS3");
        const update = _.set(
          _.cloneDeep(prodottoTemp),
          "datiUlteriori.generici.colore3",
          {
            ...prodottoTemp.datiUlteriori.generici.colore3,
            valueId: value,
            valueDesc: name,
          },
        );
        setProdottoTemp(update);
      }
    } catch (err) {
      console.log("Errore nella selezione di un colore: " + err);
      throw err;
    }
  };

  const handleChangeColoreSel = async function (evt) {
    const value = evt.target.getAttribute("data-id");
    setSalvato(false);
    try {
      if (prodottoTemp.datiUlteriori.generici.colore1.valueId == value) {
        const update = _.set(
          _.cloneDeep(prodottoTemp),
          "datiUlteriori.generici.colore1",
          {
            ...prodottoTemp.datiUlteriori.generici.colore1,
            valueId: "",
            valueDesc: "",
          },
        );
        setProdottoTemp(update);
      } else if (prodottoTemp.datiUlteriori.generici.colore2.valueId == value) {
        const update = _.set(
          _.cloneDeep(prodottoTemp),
          "datiUlteriori.generici.colore2",
          {
            ...prodottoTemp.datiUlteriori.generici.colore2,
            valueId: "",
            valueDesc: "",
          },
        );
        setProdottoTemp(update);
      } else if (prodottoTemp.datiUlteriori.generici.colore3.valueId == value) {
        const update = _.set(
          _.cloneDeep(prodottoTemp),
          "datiUlteriori.generici.colore3",
          {
            ...prodottoTemp.datiUlteriori.generici.colore3,
            valueId: "",
            valueDesc: "",
          },
        );
        setProdottoTemp(update);
      }
    } catch (err) {
      console.log("Errore nella selezione di un colore: " + err);
      throw err;
    }
  };

  //vecchia versione basata sul modulo react-dropdown-tree-select
  /* const handleChangeCategorieWeb = async function (currentNode, selectedNodes) {
        setSalvato(false);
        const arr = selectedNodes.map((el) => el.value) // aggiungo le categorie parent in server
        try {
            const update = _.set(_.cloneDeep(prodottoTemp), "categorieWebAssociate", arr)
            setProdottoTemp(update)
        } catch (err) {
            console.log("Errore nell'aggiornamento dell'albero categorie: " + err)
            throw err;
        }
    } */

  const updateProductCategoriesForSite = (
    siteKey,
    selectedNodes,
    defaultCategoryId,
  ) => {
    const config = CATEGORY_PRODUCT_SITE_FIELDS[siteKey];
    if (!config) return;
    setSalvato(false);
    try {
      const normalizedSelectedNodes = Array.isArray(selectedNodes)
        ? selectedNodes.map(String)
        : [];
      let updatedProduct = _.set(
        _.cloneDeep(prodottoTemp),
        config.categoriesField,
        normalizedSelectedNodes,
      );
      if (defaultCategoryId !== undefined) {
        updatedProduct = _.set(
          updatedProduct,
          config.defaultField,
          defaultCategoryId ? String(defaultCategoryId) : "",
        );
      }
      setProdottoTemp(updatedProduct);
    } catch (err) {
      console.error("Errore nell'aggiornamento dell'albero categorie:", err);
      throw err;
    }
  };

  const applyCategoryMappingToProduct = (
    siteKey,
    categoryIds = [],
    defaultCategoryId = "",
    options = {},
  ) => {
    const config = CATEGORY_PRODUCT_SITE_FIELDS[siteKey];
    if (!config) return false;
    const normalizedCategoryIds = (
      Array.isArray(categoryIds) ? categoryIds : []
    )
      .map(String)
      .filter(Boolean);
    if (!normalizedCategoryIds.length) return false;

    const currentCategories = Array.isArray(
      _.get(prodottoTemp, config.categoriesField),
    )
      ? _.get(prodottoTemp, config.categoriesField)
      : [];
    const currentDefault = _.get(prodottoTemp, config.defaultField);
    if (
      options.confirmOverwrite !== false &&
      (currentCategories.length || currentDefault) &&
      !window.confirm(
        `Le categorie web ${config.label} gia' valorizzate verranno sovrascritte con quelle della mappatura. Procedere?`,
      )
    ) {
      return false;
    }

    updateProductCategoriesForSite(
      siteKey,
      normalizedCategoryIds,
      defaultCategoryId || normalizedCategoryIds[0],
    );
    return true;
  };

  const handleChangeDatoUlteriore = async function (evt) {
    const value = evt.target.value;
    try {
      if (
        evt.target.name === FASCIA_SPEDIZIONE_PATH ||
        evt.target.name === METODO_SPEDIZIONE_PATH
      ) {
        const result = applicaCambioSpedizione(
          prodottoTemp, evt.target.name, value, datiUlterioriStandard,
        );
        setAvvisoSpedizione(result.avviso);
        if (result.prodotto !== prodottoTemp) {
          setProdottoTemp(result.prodotto);
          setSalvato(false);
        }
        return;
      }
      setSalvato(false);
      if (evt.target.name === "datiUlteriori.web.pubblicato.valueId") {
        setProdottoTemp(_.set(
          _.cloneDeep(prodottoTemp),
          "datiUlteriori.web.pubblicato",
          publicationValueFromSelection(value),
        ));
        return;
      }
      const update = _.set(_.cloneDeep(prodottoTemp), evt.target.name, value); // USO QUESTO METODO INVECE DI QUELLO SOPRA MA E' RESOURCE EXPENSIVE
      setProdottoTemp(update);
    } catch (err) {
      console.log("Errore nell'aggiornamento di un dato ulteriore: " + err);
      throw err;
    }
  };

  const handleChangeSciancratura = async function (
    punta,
    centro,
    coda,
    misura,
  ) {
    setSalvato(false);
    const value = `Punta ${punta} mm | Centro ${centro} mm | Coda ${coda} mm @ ${misura} cm`;
    try {
      const update = _.set(
        _.cloneDeep(prodottoTemp),
        "datiUlteriori.sci.sciancratura.valueDesc",
        value,
      ); // USO QUESTO METODO INVECE DI QUELLO SOPRA MA E' RESOURCE EXPENSIVE
      setProdottoTemp(update);
    } catch (err) {
      console.log("Errore nell'aggiornamento di un dato ulteriore: " + err);
      throw err;
    }
  };

  const handleChangeEditor = _.debounce((value) => {
    setSalvato(false);
    setProdottoTemp((prev) => ({
      ...prev,
      descrizioniSuPS: {
        ...prev.descrizioniSuPS,
        descLunga: {
          ...prev.descrizioniSuPS.descLunga,
          [lang]: value,
        },
      },
    }));
  }, 300);


  const handleChangeEditorIntro = _.debounce((value) => {
    setSalvato(false);
    setProdottoTemp((prev) => ({
      ...prev,
      descrizioniSuPS: {
        ...prev.descrizioniSuPS,
        descIntro: {
          ...prev.descrizioniSuPS?.descIntro,
          [lang]: value,
        },
      },
    }));
  }, 300);

  const leggiHtmlDaClipboard = async function () {
    if (navigator.clipboard?.read) {
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const item of clipboardItems) {
          if (item.types.includes("text/html")) {
            const htmlBlob = await item.getType("text/html");
            return await htmlBlob.text();
          }
        }
      } catch (err) {
        console.warn("Lettura HTML clipboard non disponibile:", err);
      }
    }

    if (navigator.clipboard?.readText) {
      return await navigator.clipboard.readText();
    }

    throw new Error("Clipboard API non disponibile");
  };

  const handleIncollaHtmlDescLunga = async function (e) {
    e.preventDefault();
    const descLungaAttuale = prodottoTemp?.descrizioniSuPS?.descLunga?.[lang];

    if (
      descLungaAttuale &&
      !window.confirm(
        "Esiste gia una descrizione lunga per la lingua selezionata. Vuoi sostituirla con il contenuto della clipboard?",
      )
    ) {
      return;
    }

    try {
      const clipboardHtml = await leggiHtmlDaClipboard();
      setSalvato(false);
      setProdottoTemp((prev) => ({
        ...prev,
        descrizioniSuPS: {
          ...prev.descrizioniSuPS,
          descLunga: {
            ...prev.descrizioniSuPS.descLunga,
            [lang]: clipboardHtml,
          },
        },
      }));
    } catch (err) {
      console.error("Errore nella lettura della clipboard:", err);
      window.alert("Impossibile leggere il contenuto della clipboard.");
    }
  };


  const handleIncollaHtmlDescIntro = async function (e) {
    e.preventDefault();
    const descIntroAttuale =
      prodottoTemp?.descrizioniSuPS?.descIntro?.[lang];

    if (
      descIntroAttuale &&
      !window.confirm(
        "Esiste già un testo intro per la lingua selezionata. Vuoi sostituirlo con il contenuto della clipboard?",
      )
    ) {
      return;
    }

    try {
      const clipboardHtml = await leggiHtmlDaClipboard();
      setSalvato(false);
      setProdottoTemp((prev) => ({
        ...prev,
        descrizioniSuPS: {
          ...prev.descrizioniSuPS,
          descIntro: {
            ...prev.descrizioniSuPS?.descIntro,
            [lang]: clipboardHtml,
          },
        },
      }));
    } catch (err) {
      console.error("Errore nella lettura della clipboard:", err);
      window.alert("Impossibile leggere il contenuto della clipboard.");
    }
  };

  const handleImportaDescLunga = (html) => {
    if (typeof html !== "string" || !html.trim()) return;
    setSalvato(false);
    setProdottoTemp((prev) => ({
      ...prev,
      descrizioniSuPS: {
        ...prev.descrizioniSuPS,
        descLunga: {
          ...prev.descrizioniSuPS?.descLunga,
          [lang]: html,
        },
      },
    }));
    setAttivaEditor(false);
  };

  const handleImportaDescIntro = (html) => {
    if (typeof html !== "string" || !html.trim()) return;
    setSalvato(false);
    setProdottoTemp((prev) => ({
      ...prev,
      descrizioniSuPS: {
        ...prev.descrizioniSuPS,
        descIntro: {
          ...prev.descrizioniSuPS?.descIntro,
          [lang]: html,
        },
      },
    }));
    setAttivaEditorIntro(false);
  };

  /* const handleChangeEditor = async function (value) {
        setSalvato(false);
        try {
            setProdottoTemp({
                ...prodottoTemp,
                descrizioniSuPS: {
                    ...prodottoTemp.descrizioniSuPS,
                    descLunga: {
                        ...prodottoTemp.descrizioniSuPS.descLunga,
                        [lang]: value
                    }
                }
            })
        } catch (err) {
            console.log("Errore nell'aggiornamento dell'editor (parte prodottoTemp): " + err)
            throw err;
        }
    } */

  function handleChangeAppProd(evt) {
    const value = evt.target.value;
    setAppuntiProdottoValue(value);
    setSalvato(false);
  }

  const handlePublicationDestinationsChange = (nextDestinations) => {
    const normalized = PUBLICATION_DESTINATIONS.filter((site) =>
      (Array.isArray(nextDestinations) ? nextDestinations : []).includes(site),
    );
    setPublicationDestinations(normalized);
    setPublicationDestinationsDirty(true);
    setSalvato(false);
  };

  async function salvaDestinazioniPubblicazione() {
    if (!publicationDestinationsDirty) {
      return true;
    }
    const codVS = prodottoTemp?.codiceVS?.trim();
    if (!codVS) {
      setAlertSalvataggio(
        "Codice VS mancante: impossibile salvare le destinazioni.",
      );
      return false;
    }
    try {
      await axios.patch(
        `${apiUrl}/moda/articoli/${encodeURIComponent(codVS)}/destinazioni`,
        {
          destinations: publicationDestinations,
          updatedBy: auditUsername,
        },
        {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
        },
      );
      setPublicationDestinationsDirty(false);
      return true;
    } catch (error) {
      setAlertSalvataggio(
        error?.response?.data?.error ||
          "Errore nel salvataggio delle destinazioni di pubblicazione.",
      );
      return false;
    }
  }

  const buildProductDocumentForSave = () => {
    const persistedFilters = prodottoTemp?.[CATEGORY_MAPPING_FILTERS_FIELD];
    if (
      !categoryMappingModaCorrectionsAvailable ||
      !hasPersistedCategoryMappingFilters(prodottoTemp) ||
      Object.prototype.hasOwnProperty.call(persistedFilters, "modaOverrides")
    ) {
      return { ...prodottoTemp };
    }
    // Anche senza modifiche al mapping, una fotografia legacy non deve riaccodare dati obsoleti.
    return {
      ...prodottoTemp,
      [CATEGORY_MAPPING_FILTERS_FIELD]: {
        ...categoryMappingInitialFilters,
        modaOverrides: buildModaOverrides(categoryMappingInitialFilters, categoryMappingModaValues),
      },
    };
  };

  async function salvaProdotto(e) {
    e.preventDefault();
    if (solaLettura) {
      setAlertSalvataggio("Sola lettura");
      return;
    }
    const categoriaPredefinitaVigError =
      getCategoriaPredefinitaValidationError(prodottoTemp, "VIG");
    const categoriaPredefinitaBotError =
      getCategoriaPredefinitaValidationError(prodottoTemp, "BOT");
    const categoriaPredefinitaError =
      categoriaPredefinitaVigError || categoriaPredefinitaBotError;
    if (categoriaPredefinitaError) {
      setAlertSalvataggio(categoriaPredefinitaError);
      setCategoryPageView(
        categoriaPredefinitaVigError ? "legacy-vig" : "legacy-bot",
      );
      setPagina("categorie");
      return;
    }
    if (!token) {
      console.warn("sto aspettando un token");
      return;
    }
    if (!(await salvaDestinazioniPubblicazione())) {
      return;
    }
    try {
      const { extraFlags, normalizedCurrent } = buildSavePayloadExtras();
      const data = {
        documento: buildProductDocumentForSave(),
        appunti: appuntiProdottoValue,
        utente: auditUsername,
        permessi: utente.permessiAPI,
        url_pagina_uff: prodottoTemp?.url_pagina_uff ?? "",
        ...extraFlags,
      };
      const accessToken = await getAccessTokenSilently();
      const config = {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      };
      await axios
        .patch(apiUrl + "/prodotti/prodotto/prodNS", data, config)
        .then((resp) => {
          if (resp.data.errno) {
            setAlertSalvataggio(
              `Errore nel salvataggio: ${resp.data.code} - ${resp.data.message}`,
            );
          } else {
            setAlertSalvataggio(false);
            setResultSalvataggio(resp.data);
            setSalvato(true);
            setProductFunction(prodVuoto);
            handlePostSaveSideEffects(extraFlags, normalizedCurrent);
            goBackToModa();
          }
        });
    } catch (err) {
      mostraErroreSalvataggio(err);
    }
  }

  async function salvaSenzaChiudere(e) {
    e.preventDefault();
    if (solaLettura) {
      setAlertSalvataggio("Sola lettura");
      return;
    }
    const categoriaPredefinitaVigError =
      getCategoriaPredefinitaValidationError(prodottoTemp, "VIG");
    const categoriaPredefinitaBotError =
      getCategoriaPredefinitaValidationError(prodottoTemp, "BOT");
    const categoriaPredefinitaError =
      categoriaPredefinitaVigError || categoriaPredefinitaBotError;
    if (categoriaPredefinitaError) {
      setAlertSalvataggio(categoriaPredefinitaError);
      setCategoryPageView(
        categoriaPredefinitaVigError ? "legacy-vig" : "legacy-bot",
      );
      setPagina("categorie");
      return;
    }
    if (!token) {
      console.warn("sto aspettando un token");
      return;
    }
    if (!(await salvaDestinazioniPubblicazione())) {
      return;
    }
    try {
      const { extraFlags, normalizedCurrent } = buildSavePayloadExtras();
      const data = {
        documento: buildProductDocumentForSave(),
        appunti: appuntiProdottoValue,
        utente: auditUsername,
        permessi: utente.permessiAPI,
        url_pagina_uff: prodottoTemp?.url_pagina_uff ?? "",
        ...extraFlags,
      };
      const accessToken = await getAccessTokenSilently();
      const config = {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      };
      await axios
        .patch(apiUrl + "/prodotti/prodotto/prodNS", data, config)
        .then((resp) => {
          if (resp.data.errno) {
            setAlertSalvataggio(
              `Errore nel salvataggio: ${resp.data.code} - ${resp.data.message}`,
            );
          } else {
            setAlertSalvataggio(false);
            setResultSalvataggio(resp.data);
            setSalvato(true);
            handlePostSaveSideEffects(extraFlags, normalizedCurrent);
          }
        });
    } catch (err) {
      mostraErroreSalvataggio(err);
    }
  }

  function mostraErroreSalvataggio(error) {
    const data = error?.response?.data;
    setAlertSalvataggio(
      (typeof data === "string" ? data : data?.message || data?.error?.message || data?.error) ||
      error?.message || "Errore nel salvataggio del prodotto.",
    );
  }

  function codiceProduttoreAutoSplitter(codiceIntero) {
    if (codiceIntero && codiceIntero.lastIndexOf("-") !== -1) {
      setCodProdModSuggerimento(
        codiceIntero.substring(0, codiceIntero.lastIndexOf("-")),
      );
      setCodProdColSuggerimento(
        codiceIntero.substring(
          codiceIntero.lastIndexOf("-") + 1,
          codiceIntero.length,
        ),
      );
    } else {
      if (codiceIntero) {
        setCodProdModSuggerimento(codiceIntero);
      }
      setCodProdColSuggerimento("");
    }
  }

  function accettaSuggerimenti(evt) {
    evt.preventDefault();
    setProdottoTemp({
      ...prodottoTemp,
      codiceProduttore: {
        ...prodottoTemp.codiceProduttore,
        codProduttoreModello: codProdModSuggerimento,
        codProduttoreColore: codProdColSuggerimento,
      },
    });
  }

  const closeReferenceResultsModal = () => {
    setReferenceResultsModal(null);
  };

  const selectReferenceProduct = (product) => {
    setProdottoRiferimento(product || prodVuoto);
    closeReferenceResultsModal();
  };

  const getModaMetaForReferenceProduct = async (product) => {
    const codiceVS = product?.codiceVS;
    if (!codiceVS || !token) {
      return null;
    }

    try {
      const response = await fetch(
        `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}`,
        {
          headers: { Authorization: "Bearer " + token },
        },
      );
      if (!response.ok) {
        return null;
      }
      const payload = await response.json();
      return payload?.data || null;
    } catch (err) {
      console.log("Errore nel recupero metadati MODA per reference:", err);
      return null;
    }
  };

  const handleReferenceSearchResults = async (data, searchInfo = {}) => {
    const products = Array.isArray(data) ? data.filter(Boolean) : [];
    if (products.length <= 1) {
      setProdottoRiferimento(products[0] || prodVuoto);
      return;
    }

    setReferenceResultsLoading(true);
    try {
      const productsWithMeta = await Promise.all(
        products.map(async (product) => ({
          product,
          modaMeta: await getModaMetaForReferenceProduct(product),
        })),
      );
      setReferenceResultsModal({
        ...searchInfo,
        products: productsWithMeta,
      });
    } finally {
      setReferenceResultsLoading(false);
    }
  };

  const renderReferenceResultRow = ({ product, modaMeta }, index) => {
    const codiceVS = product?.codiceVS || "-";
    const nomeIt =
      product?.shortName?.it || product?.descrizioniSuPS?.titolo?.it || "-";
    const descrizioneIt = product?.descrizioniSuPS?.descLunga?.it || "";
    const lunghezzaDescrizioneIt = descrizioneIt.length;
    const utTestiDone =
      getFirstFilledValue(
        modaMeta?.ut_testi_done,
        modaMeta?.utTestiDone,
        modaMeta?.ut_pubbl_done,
        modaMeta?.utPubblDone,
        product?.ut_testi_done,
        product?.utTestiDone,
        product?.ut_pubbl_done,
        product?.utPubblDone,
      ) || "-";
    const ultimaModifica = getFirstFilledValue(
      modaMeta?.source_updated_at,
      modaMeta?.sourceUpdatedAt,
      modaMeta?.updated_at,
      modaMeta?.updatedAt,
      product?.updated_at,
      product?.updatedAt,
      product?.ultimaModifica,
    );

    return (
      <tr
        key={product?._id || codiceVS}
        className={index % 2 === 0 ? "sfondoBiancoTema" : "bg-near-white"}
      >
        <td className="pv3 ph3 bb b--black-10">
          <button
            type="button"
            className={buttonClassesWhite}
            onClick={() => selectReferenceProduct(product)}
          >
            Scegli
          </button>
        </td>
        <td className="pv3 ph3 bb b--black-10 b">{codiceVS}</td>
        <td className="pv3 ph3 bb b--black-10">{nomeIt}</td>
        <td className="pv3 ph3 bb b--black-10 tr">{lunghezzaDescrizioneIt}</td>
        <td className="pv3 ph3 bb b--black-10">{utTestiDone}</td>
        <td className="pv3 ph3 bb b--black-10">
          {formatReferenceDate(ultimaModifica)}
        </td>
      </tr>
    );
  };

  const checkCodProd = (e) => {
    e.preventDefault();
    const codProduttore =
      prodottoTemp?.codiceProduttore?.codProduttoreModello?.trim() || "";
    if (!codProduttore) {
      setReferenceSearchFeedback("valorizza il codice modello");
      return;
    }
    setReferenceSearchFeedback("");
    fetch(apiUrl + "/prodotti/show/codiceproduttore/" + codProduttore, {
      headers: { Authorization: "Bearer " + token },
    })
      .then((resp) => resp.json())
      .then((data) =>
        handleReferenceSearchResults(data, {
          tipoRicerca: "codice produttore",
          valoreRicerca: codProduttore,
        }),
      );
  };

  const impostaCodice = (e) => {
    e?.preventDefault();
    const codVS = prodottoTemp.codiceVS;
    if (solaLettura) {
      apriProdotto(codVS, (data) => setProdottoTemp(data[0]));
      return;
    }
    fetch(apiUrl + "/prodotti/prodotto/showorcreate/" + codVS, {
      headers: { Authorization: "Bearer " + token },
    })
      .then((resp) => resp.json())
      .then((data) => setProdottoTemp(data[0]));
  };

  async function traduci(e) {
    e.preventDefault();
    try {
      if (!prodottoTemp?._id) {
        throw new Error("Salva il prodotto prima di tradurlo.");
      }
      const accessToken = await getAccessTokenSilently();
      const username = await resolveTranslationUsername({
        username: auditUsername,
        email: user?.email,
        accessToken,
      });
      const response = await axios.patch(
        `${apiUrl}/prodotti/translate/blindly`,
        {},
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { idDocProdotto: prodottoTemp._id, username },
        },
      );
      setProdottoTemp(response.data);
      setAlertSalvataggio(false);
    } catch (error) {
      const parziale = translationErrorProduct(error);
      if (parziale) setProdottoTemp(parziale);
      setAlertSalvataggio(translationErrorMessage(error));
    }
  }

  async function cambiaLingua(e) {
    e.preventDefault();
    if (!salvato) {
      if (
        !window.confirm(
          "Sei sicuro di voler cambiare lingua? Le modifiche non salvate andranno perse.",
        )
      ) {
        return; //se l'utente non conferma, esce dalla funzione senza fare nulla
      }
    }
    setAttivaEditor(false);
    setAttivaEditorIntro(false);
    let lingua = e.target.name;
    try {
      setLang(lingua);
    } catch (err) {
      console.log(err);
    }
    try {
      impostaCodice();
    } catch (err) {
      console.log(err);
    }
    //impostaCodice usa il codiceVS del prodottoTemp (che è quello attuale) per ricaricare il prodotto aggiornando la lingua
  }

  function chiudiSenzaSalvare(e) {
    e.preventDefault();
    setProductFunction(prodVuoto);
    goBackToModa();
  }

  const categorieComplete = Object.values(
    CATEGORY_PRODUCT_SITE_FIELDS,
  ).every((config) => {
    const categories = Array.isArray(prodottoTemp?.[config.categoriesField])
      ? prodottoTemp[config.categoriesField].map(String)
      : [];
    const defaultCategory = prodottoTemp?.[config.defaultField];
    return (
      categories.length > 0 &&
      defaultCategory !== undefined &&
      defaultCategory !== null &&
      categories.includes(String(defaultCategory))
    );
  });
  const haImmagineRaw =
    modaArticolo?.ha_immagine ??
    modaArticolo?.haImmagine ??
    prodottoTemp?.ha_immagine ??
    prodottoTemp?.haImmagine;
  const immaginePresente =
    immagineCompletataInSessione ||
    haImmagineRaw === true ||
    Number(haImmagineRaw) === 1;
  const descrizioneLungaPresente = Boolean(
    prodottoTemp?.descrizioniSuPS?.descLunga?.[lang]?.trim(),
  );
  const testoIntroPresente = Boolean(
    prodottoTemp?.descrizioniSuPS?.descIntro?.[lang]?.trim(),
  );
  const datiAltrovePresenti = Boolean(
    modaArticolo ||
      prestashopReport ||
      Object.values(prestashopOnline?.sites ?? {}).some(
        (siteData) => siteData?.available,
      ),
  );
  const pageCompletion = {
    categorie: categorieComplete,
    immagini: immaginePresente,
    descrizioni: descrizioneLungaPresente,
    "testo-intro": testoIntroPresente,
    "dati-altrove": datiAltrovePresenti,
  };

  ///// PAGINA

  return (
    <fieldset disabled={traduzioneInCorso} className="modificaProdottiFieldset fixed-l w-100-l colonnaTuttaPagina sfondoBiancoTema">
      {referenceResultsLoading && (
        <div className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema flex items-center justify-center">
          <div className="sfondoBiancoTema black-80 pa3 br2 shadow-5">
            Carico i risultati...
          </div>
        </div>
      )}
      <ModaArticoliAdvancedFiltersModal
        open={categoryMappingModalOpen}
        onClose={() => setCategoryMappingModalOpen(false)}
        onApply={handleApplyCategoryMappingFromFilters}
        initialFilters={categoryMappingInitialFilters}
        baselineFilters={categoryMappingBaseFilters}
        onFiltersChange={handleCategoryMappingFiltersChange}
        lookupOptions={categoryMappingLookupOptions}
        datiUlterioriStandard={datiUlterioriStandard}
        temaColori="rootTema"
        categoryMappingMode
        siteCategories={categoryMappingSiteCategories}
        initialCategoryMappingOutputs={categoryMappingInitialOutputs}
        initialCategoryMappingDefaultOutputs={
          categoryMappingInitialDefaultOutputs
        }
        onResolveExistingMappings={resolveExistingCategoryMappings}
        onApplyExistingMapping={(siteKey, categoryIds, defaultCategoryId) =>
          applyCategoryMappingToProduct(siteKey, categoryIds, defaultCategoryId)
        }
      />
      {referenceResultsModal && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema flex items-center justify-center"
          onClick={closeReferenceResultsModal}
        >
          <div
            className="sfondoBiancoTema black-80 pa4 br3 shadow-5 w-90 w-70-l flex flex-column"
            style={{ maxHeight: "80vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb3">
              <div>
                <h3 className="mt0 mb1">Seleziona reference</h3>
                <p className="ma0 f6 gray">
                  Trovati {referenceResultsModal.products.length} risultati
                  {referenceResultsModal.tipoRicerca &&
                    ` per ${referenceResultsModal.tipoRicerca}`}
                  {referenceResultsModal.valoreRicerca &&
                    ` "${referenceResultsModal.valoreRicerca}"`}
                  .
                </p>
              </div>
              <button
                type="button"
                className={classiTastoSblocco}
                onClick={closeReferenceResultsModal}
              >
                Chiudi
              </button>
            </div>
            <div className="overflow-auto flex-auto ba b--black-10 br2">
              <table className="collapse w-100">
                <thead className="sfondoColChiaro sticky top-0 z-1">
                  <tr className="tl">
                    <th className="pv3 ph3 bb b--black-20">azione</th>
                    <th className="pv3 ph3 bb b--black-20">codice VS</th>
                    <th className="pv3 ph3 bb b--black-20">nome IT</th>
                    <th className="pv3 ph3 bb b--black-20 tr">
                      lunghezza descrizione IT
                    </th>
                    <th className="pv3 ph3 bb b--black-20">
                      autore testi (prima versione)
                    </th>
                    <th className="pv3 ph3 bb b--black-20">
                      ultima data di modifica
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {referenceResultsModal.products.map(renderReferenceResultRow)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <div className=" pa3 black-80 flex flex-wrap">
        <ColonnaDatiProdotto
          prodottoTemp={prodottoTemp}
          utente={utente}
          languages={languages}
          lang={lang}
          cambiaLingua={cambiaLingua}
          tastoQuadratoGiallo={tastoQuadratoGiallo}
          tastoQuadratoVerde={tastoQuadratoVerde}
          tastoQuadratoBianco={tastoQuadratoBianco}
          alertSalvataggio={alertSalvataggio || erroreApertura}
          riquadroClassesRed={riquadroClassesRed}
          salvato={salvato}
          salvaSenzaChiudere={salvaSenzaChiudere}
          salvaProdotto={salvaProdotto}
          chiudiSenzaSalvare={chiudiSenzaSalvare}
          buttonClassesGreen={buttonClassesGreen}
          buttonClassesRed={buttonClassesRed}
          brandLimitazioniAlert={brandLimitazioniAlert}
          modaBrandNote={modaBrandNote}
          modaAnnullato={modaAnnullato}
          modaBrandDescription={modaBrandDescription}
          modaBrandCode={modaBrandCode}
          modaArticolo={modaArticolo}
          modaNomeColore={modaNomeColore}
          handleChange={handleChange}
          inputClasses={inputClasses}
          inputClassesNOW={inputClassesNOW}
          classiTastoCopiaInAlto={classiTastoCopiaInAlto}
          codProdModSuggerimento={codProdModSuggerimento}
          codProdColSuggerimento={codProdColSuggerimento}
          accettaSuggerimenti={accettaSuggerimenti}
          checkCodProd={checkCodProd}
          referenceSearchFeedback={referenceSearchFeedback}
        />
        <div className="modificaProdottiWorkspace w-100 w-70-ns w-80-l">
          <nav
            className="modificaProdottiTopNav flex"
            aria-label="Sezioni modifica prodotto"
          >
            {[
              ...(canShowImportBot && mostraImportBot
                ? [["import-bottero", "Import Bottero"]]
                : []),
              ...(canShowExportVig && mostraExportVig
                ? [["export-bottero", "Export a Bottero"]]
                : []),
              ["categorie", "Categorie"],
              ["immagini", "Immagini"],
              ["descrizioni", "Descrizioni"],
              ["testo-intro", "Testo intro"],
              ["traduzioni", "Traduzioni"],
              ["dati-altrove", "Dati altrove"],
              ["kit-moda", "Kit Moda"],
              ["impostazioni", "Impostazioni"],
              ...(canShowImportBot && !mostraImportBot
                ? [["import-bottero", "Import Bottero"]]
                : []),
              ...(canShowExportVig && !mostraExportVig
                ? [["export-bottero", "Export a Bottero"]]
                : []),
            ].map(([pageId, label]) => (
              <button
                key={pageId}
                type="button"
                className={
                  "modificaProdottiTopNavButton" +
                  (pagina === pageId
                    ? " modificaProdottiTopNavButton--active"
                    : "")
                }
                onClick={() => {
                  if (pageId === "import-bottero") setImportBotteroMounted(true);
                  if (pageId === "export-bottero") setExportBotteroMounted(true);
                  setPagina(pageId);
                }}
                aria-current={pagina === pageId ? "page" : undefined}
              >
                <span>{label}</span>
                {pageCompletion[pageId] && (
                  <span
                    className="modificaProdottiPageStatus"
                    title={label + ": dati presenti"}
                    aria-label={label + ": dati presenti"}
                  >
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      check
                    </span>
                  </span>
                )}
              </button>
            ))}
          </nav>
          {importBotteroMounted && canShowImportBot ? (
            <div
              className="modificaProdottiPagina modificaProdottiAlignmentPagina modificaProdottiImportBottero"
              hidden={pagina !== "import-bottero"}
            >
              <PrestashopAlignmentItemReview
                codVS={prodottoTemp?.codiceVS}
                token={token}
                utente={utente}
                onBeforeApply={handleBeforeAlignmentApply}
                onApplied={reloadProductAfterAlignment}
              />
            </div>
          ) : null}
          {exportBotteroMounted && canShowExportVig ? (
            <div
              className="modificaProdottiPagina modificaProdottiAlignmentPagina modificaProdottiExportBottero"
              hidden={pagina !== "export-bottero"}
            >
              <PrestashopAlignmentItemReview
                codVS={prodottoTemp?.codiceVS}
                token={token}
                utente={utente}
                direction="VIG_TO_BOT"
                onBeforeApply={handleBeforeAlignmentApply}
                onApplied={reloadProductAfterAlignment}
              />
            </div>
          ) : null}
          {pagina === "descrizioni" && (
            <Descrizioni
              product={{
                prodottoTemp,
                setProdottoTemp,
                prodottoRiferimento,
                setProdottoRiferimento,
                lang,
                utente,
              }}
              editor={{
                attivaEditor,
                setAttivaEditor,
                handleChangeEditor,
                handleIncollaHtmlDescLunga,
                handleImportaDescLunga,
              }}
              navigation={{ tab, setTab, canViewMultisiteSupport }}
              data={{
                datiUlterioriStandard,
                avvisoSpedizione,
                prestashopOnline,
                prestashopOnlineLoading,
                prestashopOnlineError,
                appuntiProdottoValue,
                listaNomiProdotto,
                token,
                auditUsername,
                modaBrandCode,
              }}
              actions={{
                handleChangeAppProd,
                handleReferenceSearchResults,
                handleChange,
                handleChangeDatoUlteriore,
                handleChangeColoreSel,
                handleChangeColoreNonSel,
                handleChangeSciancratura,
                traduci,
                onImmaginiCopiate: handleUploadImmaginiSuccess,
              }}
              ui={{
                inputClasses,
                inputClassesConDueTasti,
                buttonClassesWhite,
                buttonClassesRed,
                classiTastoCopiaInAlto,
                classiTastoSblocco,
                vociMenuTabs,
                vociMenuTabsSelezionata,
              }}
            />
          )}
          {pagina === "traduzioni" && (
            <Traduzioni
              key={prodottoIdentity}
              prodotto={prodottoTemp}
              utente={utente}
              salvato={salvato && !publicationDestinationsDirty}
              tradPermessa={props.tradPermessa}
              siti={publicationDestinations}
              refreshKey={resultSalvataggio}
              buttonClass={buttonClassesWhite}
              onBusy={setTraduzioneInCorso}
              onTranslated={(product) => {
                setProdottoTemp(product);
                setSalvato(true);
                setAlertSalvataggio(false);
                if (typeof setProductFunction === "function") setProductFunction(product);
              }}
            />
          )}
          {pagina === "testo-intro" && (
            <TestoIntro
              prodottoTemp={prodottoTemp}
              lang={lang}
              editorType={utente.textEditor}
              active={attivaEditorIntro}
              onChange={handleChangeEditorIntro}
              onActivate={() => setAttivaEditorIntro(true)}
              onPasteHtml={handleIncollaHtmlDescIntro}
              onImportIntro={handleImportaDescIntro}
              prestashopOnline={prestashopOnline}
              prestashopOnlineLoading={prestashopOnlineLoading}
              prestashopOnlineError={prestashopOnlineError}
              buttonClassesWhite={buttonClassesWhite}
            />
          )}
          {pagina === "categorie" && (
            <Categorie
              prodottoTemp={prodottoTemp}
              view={categoryPageView}
              onViewChange={setCategoryPageView}
              siteFields={CATEGORY_PRODUCT_SITE_FIELDS}
              siteCategories={categoryMappingSiteCategories}
              datiUlterioriStandard={datiUlterioriStandard}
              inputClasses={inputClasses}
              buttonClassesWhite={buttonClassesWhite}
              mappingSaving={categoryMappingSaving}
              mappingFeedback={categoryMappingFeedback}
              onOpenMapping={() => {
                setCategoryMappingFeedback("");
                setCategoryMappingModalOpen(true);
              }}
              onFiltersChange={handleCategoryMappingFiltersChange}
              initialFilters={categoryMappingInitialFilters}
              baselineFilters={categoryMappingBaseFilters}
              lookupOptions={categoryMappingLookupOptions}
              initialMappingTab={categoryMappingInitialTab}
              mappingRequirements={categoryMappingRequirements}
              onResolveExistingMappings={resolveExistingCategoryMappings}
              onApplyExistingMapping={(siteKey, categoryIds, defaultCategoryId) =>
                applyCategoryMappingToProduct(
                  siteKey,
                  categoryIds,
                  defaultCategoryId,
                )
              }
              onChangeDefaultCategory={handleChangeDatoUlteriore}
              onChangeSiteCategories={updateProductCategoriesForSite}
              getValidationError={getCategoriaPredefinitaValidationError}
            />
          )}
          {pagina === "immagini" && (
            <Immagini
              product={prodottoTemp}
              token={token}
              uploadedBy={
                auditUsername || undefined
              }
              brandCode={modaBrandCode}
              botProdIDps={modaArticolo?.idPsBot}
              refreshKey={refreshImmaginiKey}
              canViewDebugDetails={Boolean(
                utente?.permessiAPI?.["read:debug"],
              )}
              onCompleted={handleUploadImmaginiSuccess}
            />
          )}
          {pagina === "dati-altrove" && (
            <DatiAltrove
              prestashopOnline={prestashopOnline}
              prestashopOnlineLoading={prestashopOnlineLoading}
              prestashopOnlineError={prestashopOnlineError}
              modaArticolo={modaArticolo}
              modaArticoloLoading={modaArticoloLoading}
              modaArticoloError={modaArticoloError}
              prestashopReport={prestashopReport}
              prestashopReportLoading={prestashopReportLoading}
              prestashopReportError={prestashopReportError}
              formatLabel={formatModaLabel}
              formatValue={formatModaValue}
              modaHiddenKeys={modaHiddenKeys}
            />
          )}
          {pagina === "kit-moda" && (
            <KitModa codVS={prodottoTemp?.codiceVS} utente={utente} />
          )}
          {pagina === "impostazioni" && (
            <Impostazioni
              destinations={publicationDestinations}
              onDestinationsChange={handlePublicationDestinationsChange}
              disabled={!utente?.permessiAPI?.["update:database"]}
            />
          )}
        </div>
      </div>
    </fieldset>
  );
}

export default ModificaProdottiNEW;
